import { api, fetchWithAuth } from './api';
import type {
  ContentGeneration,
  StatVariant,
  ContentStylePreset,
  ContentRatingsSummary,
  ContentPosted,
  ContentType,
  ContentRatingValue,
  ImageTemplate,
  CarouselData,
  CarouselSlide,
  StockPhoto,
  StockWarning,
  AiGeneratedImage,
  ContentFormatDef,
  TemplateRanking,
  ContentPlan,
  ContentPlanSlot,
  PlanChangelog,
  FormatPerformance,
  RuleSuggestion,
  CriticStats,
  AIAnalysis,
  BatchTopic,
  MultiPlatformGenerationRequest,
  ContentExperiment,
  ExperimentSuggestion,
  ExperimentMatrix,
  WinningPatterns,
  NicheInsights,
  WeekSlotSuggestion,
  WeekSlotGeneration,
  WeekPlanConfirmResult,
  SlideOverrides,
  CustomTemplate,
  DesignPreset,
  BlockLayout,
  BlockLayoutRole,
} from '../types';

// ---- Prompt-Inspector Datenfluss-Artefakte ------------------------------
// Spiegel von preview-prompt `artifacts`: was tatsaechlich in den Prompt floss.
export interface PromptArtifacts {
  inputs?: {
    content_type?: string;
    content_lang?: string;
    topic_angle?: string | null;
    tension_category?: string | null;
    hook_formula?: string | null;
    preset?: string | null;
    experiments?: string[];
    competitor_post_count?: number;
    custom_rules?: string | null;
  };
  grounding_evidence?: { title?: string; cluster?: string; source_slug?: string; phase?: string; snippet?: string }[];
  competitor_posts?: { username?: string; caption?: string }[];
  // Reichere Artefakte aus dem vollen Generate-Flow (nicht im Preview):
  document_evidence?: { title?: string; snippet?: string }[];
  per_point_evidence?: { point?: string; evidence_count?: number; snippets?: string[] }[];
  viral_hook?: {
    id?: number | string;
    category?: string;
    name?: string;
    template?: string;
    source?: string;
    drift?: { match?: boolean; score?: number; reason?: string };
  } | null;
}

// ---- Value-Anchor Gate (Stage 0) ----------------------------------------
// Schema-Spiegel von api/routers/content_studio.py::_ValueAnchor.
// schema_version=1 · bei Backend-Bump muessen Reader graceful skippen.

export type AnchorKind =
  | 'chat_example'
  | 'side_by_side'
  | 'example_list'
  | 'framework'
  | 'number'
  | 'quote';

export type AnchorVisual = 'diagram' | 'example' | 'text';
export type AnchorSource = 'auto' | 'manual';
export type AnchorListMarker = '✗' | '✓' | '→' | '•' | '1.' | '2.' | '3.' | '4.' | '5.';

// Visual presets fuer Example-Templates. Mirrored from Diagramm-Presets fuer
// konsistente Mixed-Carousel Rendering.
export type AnchorStylePreset =
  | 'consulting_navy'
  | 'tech_mono'
  | 'editorial_cream'
  | 'corporate_blue'
  | 'minimal_mono';

export interface AnchorChatMsg {
  side: 'left' | 'right';
  text: string;
  label?: string;
}

export interface AnchorChatPayload {
  type: 'chat_example';
  variant: 'wrong_vs_right' | 'sequence';
  headline?: string;
  messages: AnchorChatMsg[];
  style_preset?: AnchorStylePreset;
}

export interface AnchorListPayload {
  type: 'example_list';
  headline?: string;
  items: Array<{ marker: AnchorListMarker; text: string }>;
  style_preset?: AnchorStylePreset;
}

export interface AnchorSplitPayload {
  type: 'side_by_side';
  headline?: string;
  left: { label: string; items: string[] };
  right: { label: string; items: string[] };
  style_preset?: AnchorStylePreset;
}

export type AnchorPayload = AnchorChatPayload | AnchorListPayload | AnchorSplitPayload;

export interface ValueAnchor {
  schema_version: 1;
  kind: AnchorKind;
  title: string;
  content: string;
  payload: AnchorPayload | null;
  source: AnchorSource;
  source_insight_id: number | null;
  suggested_visual: AnchorVisual;
  target_slide_role: 'body';
  target_slide_index: number | null;
}

/** Layout-Budget warning surfaced from the wizard body-options response.
 *  See services/layout_budget.py::LayoutWarning + services/brand_kit_layer.py::KitWarning. */
export type LayoutWarningType =
  | 'shrunk'                 // decorative content silently truncated
  | 'split'                  // block partitioned across 2 slides
  | 'rejected'               // schema/structural error · caller falls back
  | 'needs_human_decision'   // substantive overflow requiring user choice
  | 'shrunk_lossy'           // substantive content force-shrunk (old-client fallback)
  | 'kit_invalid_color'
  | 'kit_low_contrast'
  | 'kit_unknown_font'
  | 'kit_palette_too_many'
  | 'kit_missing_palette'
  | 'kit_logo_invalid';

export interface LayoutWarning {
  severity: 'info' | 'warn';
  type: LayoutWarningType;
  block_index?: number;
  field_path: string;
  detail: string;
  original_chars?: number;
  final_chars?: number;
  /** Set when type==='needs_human_decision'. Tells the frontend which 3 buttons
   *  to render so the user can pick: split / accept_shrink_with_loss / edit_manually. */
  options_for_user?: Array<'accept_split' | 'accept_shrink_with_loss' | 'edit_manually'>;
}

/** Wizard body-option entry (each item in the response.options array). */
export interface BodyOption {
  text: string;
  angle?: string;
  visual_hint?: string;
  blocks?: any[];
  composition_version?: number;
  /** Only present when layout-budget transformed the blocks. Frontend should
   *  show a small notice; substantive transformations have severity='warn'. */
  layout_warnings?: LayoutWarning[];
  /** Original input blocks before any transformation (provenance). Only set
   *  when layout-budget mutated the primary blocks list. */
  original_blocks?: any[];
  /** Compact summary of layout-budget actions applied. */
  transformation_summary?: { usage_pct: number; actions: string[] };
  /** Additional slides emitted by SPLIT path. Only populated when client
   *  requested supports_human_decision=true. */
  extra_slides?: any[][];
}

// Backend reichert Auto-Anker mit last_used_at fuer Reuse-Badge an.
export interface ValueAnchorWithReuse extends ValueAnchor {
  last_used_at: string | null;
}

// ---- Family Stability ---------------------------------------------------

export type FamilyOverrideMode = 'one_off' | 'experiment' | 'adopt_incumbent';

export interface FamilyPosterior {
  family: string;
  label: string;
  alpha: number;
  beta: number;
  n: number;
  mean: number;
  is_incumbent: boolean;
  is_preferred: boolean;
}

export interface FamilyPosteriorResponse {
  strategy_id: number;
  incumbent_family: string | null;
  preferred_family: string | null;
  decision_token: string;
  posteriors: FamilyPosterior[];
  stability_enabled: boolean;
}

export interface FamilyPickerOption {
  family: string;
  label: string;
  score: number;
  longest_match: number;
  keywords: string[];
  is_recommended: boolean;
  is_top3: boolean;
}

export interface FamilyPickerResponse {
  strategy_id: number;
  preferred_family: string | null;
  niche: string;
  has_palette: boolean;
  has_logo: boolean;
  families: FamilyPickerOption[];
}

export interface FamilyOverrideDetail {
  code: 'family_override_required' | 'family_override_token_stale';
  required: boolean;
  incumbent: { family: string; alpha: number; beta: number; n: number; mean: number };
  challenger: { family: string; alpha: number; beta: number; n: number; mean: number };
  signals: {
    sticky_margin: number;
    sticky_threshold: number;
    expected_loss: number;
    expected_loss_threshold: number;
    prob_challenger_better: number;
  };
  recommendation: 'accept' | 'reject';
  recommendation_text: string;
  decision_token: string;
}

export interface FamilyDecisionRow {
  id: number;
  strategy_id: number;
  generation_id: number | null;
  decided_at: string;
  source: string;
  eligible_families: string[];
  chosen_family: string;
  incumbent_family: string | null;
  sticky_margin: number | null;
  sticky_threshold: number | null;
  expected_loss: number | null;
  expected_loss_threshold: number | null;
  prob_challenger_better: number | null;
  rationale: string;
  actor_user_id: number | null;
  arm_state_hash: string;
}

export interface CourseIdea {
  slug: string;
  title: string;
  core_concept: string;
  phase: string;
  phase_label: string;
  relevance_score: number;
}

export interface CourseLesson extends CourseIdea {
  phase_order: number;
  lesson_order: number;
  insight_count: number;
  used_count?: number;
  total_angles?: number;
}

export interface CourseLessonInsight {
  insight_id: string;
  title: string;
  insight_text: string;
  cluster: string;
}

export interface ReExtractResponse {
  slug: string;
  insight_count_before: number;
  insight_count_after: number;
  insights: { title: string; insight_text: string }[];
  status: 'ok' | 'no_insights_extracted';
}

export interface ProgressionNext {
  lesson_slug: string;
  lesson_title: string;
  phase_label: string;
  insight_id: string | null;
  insight_title: string | null;
  insight_text: string | null;
}

export interface QuickPreviewResponse {
  hook_text: string;
  slide_text: string;
  slide_data: Record<string, unknown>;
  slide_position: number;
  slide_role: 'hook' | 'content' | 'cta';
  model_used: string;
  quality_criteria: {
    aggressiveness: { value: number; label: string };
    aspect_focus: { value: string; label: string };
    tone_formality: { value: number; label: string };
    tension_category: string | null;
    hook_formula: string | null;
    course_context: { title: string; core_concept: string; phase: string } | null;
  };
  narrative_connection: string;
  key_influences: string[];
  confidence_score: number;
  confidence_reasoning: string;
}

export interface PreviousSlide {
  number: number;
  content_type: string;
  text: string;
}

export interface DirectionOption {
  direction: string;
  description: string;
  suggested_content_type: string;
  confidence: number;
  pros: string[];
  cons: string[];
}

export interface BlueprintSlot {
  position: number;
  content_type: string;
  reasoning: string;
  confidence: number;
  risk_warning: string;
  alternatives: Array<{
    content_type: string;
    reasoning: string;
    pros: string[];
    cons: string[];
    risk_warning: string;
  }>;
}

export interface BlueprintResponse {
  slots: BlueprintSlot[];
  total_slides: number;
  niche: string;
  has_approved_stats: boolean;
  approved_stats_count: number;
}

export interface SlideFeedbackPayload {
  verdict: 'like' | 'dislike';
  comment?: string;
  slide_position: number;
  slide_role: string;
  slide_content_type: string;
  slide_text: string;
  aggressiveness: number;
  aspect_focus: string;
  tone_formality: number;
  mehrwert?: number;
  tension_category?: string | null;
  hook_formula?: string | null;
  chosen_direction?: string;
  content_type: string;
  experiments?: string;
  blueprint_snapshot?: string;
  slide_data?: string;
}

export interface TasteProfile {
  defaults: { aggressiveness?: number; aspect_focus?: string; tone_formality?: number; mehrwert?: number };
  dimensions: Record<string, Array<{ value: string; wilson: number; likes: number; dislikes: number; total: number }>>;
  total_hypotheses: number;
  confidence: 'low' | 'medium' | 'high';
}

export interface PoolStats {
  total: number;
  pending: number;
  used: number;
  skipped: number;
  failed: number;
  sources: { source: string; count: number }[];
}

export interface TensionDef {
  id: string;
  label: string;
  description: string;
  prompt_text: string;
}

export interface HookFormulaDef {
  id: string;
  label: string;
  description: string;
  prompt_text: string;
}

export interface FreeSlot {
  slot_id: number;
  day: string;
  time_window: string;
  scheduled_at: string;
  label: string;
}

export interface ExampleEntry {
  hook: string;
  description: string;
}

export interface EngineExamples {
  tensions: Record<string, ExampleEntry>;
  hook_formulas: Record<string, ExampleEntry>;
  prompt_lab: Record<string, string>;
}

export interface SubItemScore {
  text: string;
  score: number;
  reason: string;
  improve: string;
}

export interface SectionScore {
  section: string;
  score: number;
  items: SubItemScore[];
}

export interface LayerScore {
  score: number;
  confidence: number;
  reason: string;
  improve: string;
  impact: string;
  prompt_weight: number;
  active: boolean;
  sub_scores?: SectionScore[];
}

export interface CockpitScores {
  overall: number;
  overall_confidence: number;
  layers: Record<string, LayerScore>;
  weak_layers: string[];
  improvable_layers: string[];
}

export interface KnowledgePanelBook {
  id: number;
  filename: string | null;
  insight_count: number;
  pillars: string[];
  pillars_status: 'ready' | 'queued' | 'running' | 'failed' | 'missing';
  extraction_status: string | null;
}

export interface KnowledgePanel {
  books: KnowledgePanelBook[];
  totals: { books: number; pillars: number; insights: number };
}

export interface EngineConfig {
  tensions: TensionDef[];
  hook_formulas: HookFormulaDef[];
  niche_directive: {
    name: string;
    content: string;
    preferred_tensions: string[];
    source?: 'override' | 'niche' | 'none';
  };
  strategy_context: {
    tensions: string[];
    secret_desire: string;
    own_language: string;
    message_equation: string;
    words_resonated: string;
    core_problem: string;
    transformation_after: string;
    rich_context: {
      occupation: string;
      backstory: string;
      purchase_trigger: string;
      failed_attempts: string;
      cost_of_staying: string;
      signal_vs_noise: { signal: string; noise: string }[];
    };
    content_strategy: {
      active_phase: {
        name: string;
        goal: string;
        guidelines: string;
        pillar_weights: { name: string; weight: number }[];
      } | null;
      narrative_arc: string;
      content_pillars: string[];
    };
  };
  archetype: {
    failure_scene: string;
    identity_tension: string;
    insight_gap: string;
    winning_scene: string;
  };
  persona?: {
    posting_persona: string;
    posting_persona_notes: string;
    account_stage: string;
    author_role?: string;
    author_proof_points?: string;
    author_voice_samples?: string;
    posting_persona_account_id?: number | string | null;
  };
  template_families: { id: string; label: string; locked: boolean }[];
  next_experiment: {
    tension_category: string;
    hook_formula: string;
    is_exploit: boolean;
    reasoning: string;
  } | null;
  next_free_slots: FreeSlot[];
  dedup_recent: { tension: string; hook: string; preview: string }[];
  impact_descriptions: Record<string, string>;
  examples: EngineExamples;
  has_curriculum?: boolean;
  documents?: EngineDocument[];
  capabilities?: {
    sharp_rules_hook_types: string[];
    sharp_rules_caption_types: string[];
  };
}

export interface EngineDocument {
  id: number;
  original_filename: string;
  file_type: string;
  file_size: number;
  extraction_status: 'pending' | 'processing' | 'done' | 'error';
  extraction_error?: string | null;
  insight_used?: number;
  insight_total?: number;
  niche_id?: number | null;
  niche_name?: string | null;
  is_niche_shared?: boolean;
  default_scope?: 'private' | 'niche' | 'account';
}

// ---- Auto-Pilot types -------------------------------------------------------
export type AutoPilotReuse = 'foundational' | 'situational' | 'novelty';

export interface AutoPilotInsight {
  id: number;
  title: string;
  insight_text?: string;
  cluster?: string | null;
  chapter_label?: string | null;
  chapter_index?: number | null;
  reusability?: AutoPilotReuse | null;
  used_count?: number;
  status?: string;
}

export interface AutoPilotTopicCluster {
  id: number;
  pillar_id: number | null;
  topic_title: string;
  bullets: string[];
  insight_ids: number[];
  insights: AutoPilotInsight[];
  contributing_pillars: number[];
  contributing_chapters: string[];
  confidence: number | null;
  status: string;
  created_at?: string;
  decided_at?: string | null;
}

export interface AutoPilotSynthJob {
  run_id: string;
  strategy_id: number;
  kind: 'pillar_extract' | 'topic_synth';
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  error?: string | null;
  inserted_count: number;
  created_at: string;
  updated_at: string;
}

export interface AutoPilotLibraryBook {
  id: number;
  original_filename: string;
  file_type: string;
  file_size: number;
  insight_count: number;
  pillar_count: number;
  pillar_language?: string | null;
  extraction_status: string;
  created_at: string;
}

export const contentStudioApi = {
  previewPrompt: (
    strategyId: number,
    data: {
      content_type: ContentType;
      preset_id?: number;
      custom_rules?: string;
      competitor_post_ids?: number[];
      content_lang?: string;
      channel_id?: number;
      experiments?: string[];
      topic_angle?: string;
      tension_category?: string;
      hook_formula?: string;
    }
  ) =>
    api.post<{ system_prompt: string; user_message: string; artifacts?: PromptArtifacts }>(
      `/content-studio/${strategyId}/preview-prompt`,
      data
    ),

  generate: (
    strategyId: number,
    data: {
      content_type: ContentType;
      preset_id?: number;
      custom_rules?: string;
      model?: string;
      prompt_override?: string | null;
      competitor_post_ids?: number[];
      content_lang?: string;
      channel_id?: number;
      experiments?: string[];
      topic_angle?: string;
      test_text?: string;
      tension_category?: string;
      hook_formula?: string;
      viral_hook_id?: string;
      viral_hook_category?: string;
      auto_sequence?: boolean;
      auto_slot?: boolean;
      preview_slides?: Array<{ text: string; role: string; content_type?: string; data?: Record<string, unknown> }>;
      aggressiveness?: number;
      aspect_focus?: string;
      tone_formality?: number;
      mehrwert?: number;
      /** Kontrast-Metrik-Modus: akkumulierender Ist-vs-Ziel-Kontrast. */
      metric_accumulation?: boolean;
      selected_course_slug?: string | null;
      selected_insight_id?: string | null;
      document_ids?: number[];
      excluded_book_ids?: number[];
      included_book_ids?: number[];
      /** Phase 2.10 · global block-layout preference for body slides.
       *  "auto"/undefined = AI free choice via cookbook decision-tree.
       *  Otherwise must be a key from LAYOUT_PRESETS (e.g. "numbered_steps",
       *  "stat_hero", "comparison", "list", "quote", "timeline", "faq",
       *  "diagram", "image_split", "hook_image"). */
      body_layout_preference?: string;
      /** Phase 1 (warm-dijkstra) · per-role layout preferences. Same
       *  semantics as body_layout_preference but applied to hook/cta slides. */
      hook_layout_preference?: string;
      cta_layout_preference?: string;
    }
  ) =>
    api.post<{ generation: ContentGeneration; additional_generations?: ContentGeneration[]; stat_variants?: StatVariant[]; plan_slot_id?: number | null; slot_label?: string | null; scheduled_at?: string | null }>(`/content-studio/${strategyId}/generate`, data),

  refine: (strategyId: number, genId: number, feedback: string, model?: string, opts?: { preview?: boolean; prompt_override?: string; prompt_system_override?: string }) =>
    api.post<{ generation?: ContentGeneration; preview?: boolean; prompt_system?: string; user_message?: string }>(
      `/content-studio/${strategyId}/generations/${genId}/refine`,
      { feedback, model, ...(opts ?? {}) }
    ),

  regenerate: (strategyId: number, genId: number, opts?: { model?: string; variantsCount?: number; preserveTemplate?: boolean }) =>
    api.post<{ generation: ContentGeneration }>(
      `/content-studio/${strategyId}/generations/${genId}/regenerate`,
      {
        model: opts?.model,
        variants_count: opts?.variantsCount ?? 1,
        preserve_template: opts?.preserveTemplate ?? true,
      }
    ),

  listGenerations: (
    strategyId: number,
    params?: { content_type?: ContentType; starred_only?: boolean; limit?: number; offset?: number; experiments?: string[] }
  ) => {
    const q = new URLSearchParams();
    if (params?.content_type) q.set('content_type', params.content_type);
    if (params?.starred_only) q.set('starred_only', 'true');
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    if (params?.experiments?.length) q.set('experiments', params.experiments.join(','));
    return api.get<{ items: ContentGeneration[]; total: number }>(
      `/content-studio/${strategyId}/generations?${q}`
    );
  },

  getChain: (strategyId: number, genId: number) =>
    api.get<{ chain: ContentGeneration[] }>(
      `/content-studio/${strategyId}/generations/${genId}/chain`
    ),

  updateGeneration: (strategyId: number, genId: number, data: Partial<ContentGeneration>) =>
    api.patch<ContentGeneration>(`/content-studio/${strategyId}/generations/${genId}`, data),

  // ── Design Decoupling ────────────────────────────────────────────────────
  getDesignPresets: () =>
    api.get<DesignPreset[]>('/content-studio/design-presets'),

  getBlockLayouts: (role?: BlockLayoutRole) =>
    api.get<BlockLayout[]>(
      role ? `/content-studio/block-layouts?role=${role}` : '/content-studio/block-layouts'
    ),

  setGenerationDesign: (strategyId: number, genId: number, designId: string | null) =>
    api.patch<ContentGeneration>(`/content-studio/${strategyId}/generations/${genId}`, { design_id: designId }),

  deleteGeneration: (strategyId: number, genId: number) =>
    api.delete(`/content-studio/${strategyId}/generations/${genId}`),

  rate: (strategyId: number, genId: number, rating: ContentRatingValue, notes?: string) =>
    api.post<{ id: number }>(
      `/content-studio/${strategyId}/generations/${genId}/rate`,
      { rating, notes }
    ),

  getRatingsSummary: (strategyId: number) =>
    api.get<ContentRatingsSummary>(`/content-studio/${strategyId}/ratings/summary`),

  getPresets: (strategyId: number) =>
    api.get<ContentStylePreset[]>(`/content-studio/${strategyId}/presets`),

  createPreset: (strategyId: number, data: Partial<ContentStylePreset>) =>
    api.post<ContentStylePreset>(`/content-studio/${strategyId}/presets`, data),

  updatePreset: (strategyId: number, presetId: number, data: Partial<ContentStylePreset>) =>
    api.patch<ContentStylePreset>(`/content-studio/${strategyId}/presets/${presetId}`, data),

  deletePreset: (strategyId: number, presetId: number) =>
    api.delete(`/content-studio/${strategyId}/presets/${presetId}`),

  getSettingsPerformance: (strategyId: number) =>
    api.get<{
      insufficient_data: boolean;
      total_hypotheses: number;
      dimension_stats?: Record<string, Array<{ value: string; win_rate: number; wilson: number; likes: number; dislikes: number; total: number; confidence: string }>>;
      top_combos?: Array<{ aggressiveness: string; aspect_focus: string; tone_formality: string; win_rate: number; wilson: number; total: number }>;
      bottom_combos?: Array<{ aggressiveness: string; aspect_focus: string; tone_formality: string; win_rate: number; wilson: number; total: number }>;
      thompson_arms?: Array<{ family: string; alpha: number; beta: number; total_pulls: number }>;
      ratings?: { total: number; good_count: number; bad_count: number; adjust_count: number };
    }>(`/content-studio/${strategyId}/settings-performance`),

  getCompetitorPosts: (strategyId: number, limit = 5) =>
    api.get<{ posts: Array<{ id: number; caption: string; viral_score: number; owner_username: string }> }>(
      `/content-studio/${strategyId}/competitor-posts?limit=${limit}`
    ),

  markPosted: (strategyId: number, genId: number, post_url: string, scrape_intervals: string[]) =>
    api.post<ContentPosted>(
      `/content-studio/${strategyId}/generations/${genId}/mark-posted`,
      { post_url, scrape_intervals }
    ),

  getEngagement: (strategyId: number, genId: number) =>
    api.get<ContentPosted | null>(
      `/content-studio/${strategyId}/generations/${genId}/engagement`
    ),

  listTemplates: () => api.get<ImageTemplate[]>('/content-studio/templates'),

  // Carousel
  getCarouselSlides: (strategyId: number, genId: number) =>
    api.get<CarouselData>(`/content-studio/${strategyId}/generations/${genId}/slides`),

  updateSlideText: (strategyId: number, genId: number, slideNumber: number, slide_text: string) =>
    api.patch<{ ok: boolean; blocks_synced?: boolean }>(`/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}`, { slide_text }),

  /** Remove ALL ParagraphBlocks from the slide's composition blocks.
   *  Headline stays. Marks slide is_stale → next render shows the cleaned layout. */
  clearSlideBodyText: (strategyId: number, genId: number, slideNumber: number) =>
    api.delete<{ ok: boolean; removed: number }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/body-text`,
    ),

  rewriteSlide: (
    strategyId: number,
    genId: number,
    slideNumber: number,
    data?: { content_lang?: string; template_id?: string; user_instruction?: string; preview?: boolean; prompt_override?: string; prompt_system_override?: string }
  ) =>
    api.post<{ slide_text?: string; version?: number | null; user_instruction?: string | null; preview?: boolean; prompt_system?: string; user_message?: string }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/rewrite`,
      data || {}
    ),

  listSlideVersions: (strategyId: number, genId: number, slideNumber: number) =>
    api.get<{ versions: Array<{ id: number; version: number; slide_text: string; user_instruction: string | null; created_at: string }> }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/versions`
    ),

  restoreSlideVersion: (strategyId: number, genId: number, slideNumber: number, version: number) =>
    api.post<{ slide_text: string; version: number }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/versions/${version}/restore`,
      {}
    ),

  generateSlideImage: async (
    strategyId: number,
    genId: number,
    slideNumber: number,
    data: { template_id?: string; image_url?: string; logo_url?: string; language?: string }
  ): Promise<{ image_path: string; overflow_warning?: string; alternative_templates?: string[]; extra_slides_created?: number }> => {
    return api.post<{ image_path: string; overflow_warning?: string; alternative_templates?: string[]; extra_slides_created?: number }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/generate-image`,
      data
    );
  },

  deleteSlide: (strategyId: number, genId: number, slideNumber: number) =>
    api.delete(`/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}`),

  insertSlide: (strategyId: number, genId: number, at_position: number, slide_text = '') =>
    api.post<{ slides: CarouselSlide[] }>(`/content-studio/${strategyId}/generations/${genId}/slides/insert`, { at_position, slide_text }),

  updateCarouselMeta: (strategyId: number, genId: number, data: { carousel_caption?: string; carousel_hashtags?: string }) =>
    api.patch<{ ok: boolean }>(`/content-studio/${strategyId}/generations/${genId}/carousel-meta`, data),

  rewriteCarouselCaption: (strategyId: number, genId: number, content_lang?: string, opts?: { preview?: boolean; prompt_override?: string; prompt_system_override?: string }) =>
    api.post<{ caption?: string; hashtags?: string; preview?: boolean; prompt_system?: string; user_message?: string }>(`/content-studio/${strategyId}/generations/${genId}/rewrite-caption`, { content_lang, ...(opts ?? {}) }),

  changeTemplate: (
    strategyId: number,
    genId: number,
    template_id: string,
    override?: { override_mode: FamilyOverrideMode; decision_token: string },
  ) =>
    api.patch<{ ok: boolean; template_id: string; override_mode: FamilyOverrideMode | null }>(
      `/content-studio/${strategyId}/generations/${genId}/template`,
      { template_id, ...(override || {}) },
    ),

  updateSlideTemplate: (
    strategyId: number,
    genId: number,
    slideNumber: number,
    templateId: string | null,
    override?: { override_mode: FamilyOverrideMode; decision_token: string },
  ) =>
    api.patch<{ ok: boolean; override_mode: FamilyOverrideMode | null }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/template`,
      { template_id: templateId, ...(override || {}) },
    ),

  updateCoverTemplate: (
    strategyId: number,
    genId: number,
    templateId: string | null,
  ) =>
    api.patch<{ ok: boolean; cover_template_id: string | null }>(
      `/content-studio/${strategyId}/generations/${genId}/cover-template`,
      { template_id: templateId },
    ),

  getFamilyPosteriors: (strategyId: number) =>
    api.get<FamilyPosteriorResponse>(`/content-studio/strategies/${strategyId}/family-posteriors`),

  getFamilyPicker: (strategyId: number) =>
    api.get<FamilyPickerResponse>(`/content-studio/strategies/${strategyId}/family-picker`),

  selectFamilyPicker: (strategyId: number, family: string) =>
    api.post<{ ok: boolean; preferred_family: string }>(
      `/content-studio/strategies/${strategyId}/family-picker`,
      { family },
    ),

  fetchFamilyPickerPreview: async (
    strategyId: number,
    family: string,
    lang: string = 'de',
  ): Promise<string> => {
    const token = localStorage.getItem('token');
    const url = `/api/content-studio/strategies/${strategyId}/family-picker/preview?family=${encodeURIComponent(family)}&language=${encodeURIComponent(lang)}`;
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Preview render failed: ${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },

  /** Direct template-id preview render. Reuses the same endpoint as family-picker
   *  but renders any registered HTML template (no eligibility filter).
   *  Caller is responsible for URL.revokeObjectURL on cleanup. */
  fetchTemplatePreview: async (
    strategyId: number,
    templateId: string,
    lang: string = 'de',
    signal?: AbortSignal,
  ): Promise<string> => {
    const token = localStorage.getItem('token');
    const url = `/api/content-studio/strategies/${strategyId}/family-picker/preview?template_id=${encodeURIComponent(templateId)}&language=${encodeURIComponent(lang)}`;
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal,
    });
    if (!res.ok) throw new Error(`Preview render failed: ${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },

  getFamilyDecisions: (strategyId: number, limit = 20) =>
    api.get<{ strategy_id: number; decisions: FamilyDecisionRow[] }>(
      `/content-studio/strategies/${strategyId}/family-decisions?limit=${limit}`,
    ),

  listThemeFamilies: () =>
    api.get<{ theme_families: Array<{ id: string; label: string; font_sample: string }> }>(`/content-studio/theme-families`),

  setGenerationTheme: (strategyId: number, genId: number, themeFamily: string | null) =>
    api.patch<{ ok: boolean; theme_family: string | null }>(
      `/content-studio/${strategyId}/generations/${genId}/theme`,
      { theme_family: themeFamily },
    ),

  setGenerationBgColor: (strategyId: number, genId: number, bgColor: string | null) =>
    api.patch<{ ok: boolean; bg_color: string | null }>(
      `/content-studio/${strategyId}/generations/${genId}/bg-color`,
      { bg_color: bgColor },
    ),

  setSlideTheme: (strategyId: number, genId: number, slideNumber: number, themeFamily: string | null) =>
    api.patch<{ ok: boolean; theme_family: string | null }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/theme`,
      { theme_family: themeFamily },
    ),

  updateSlideOverlays: (strategyId: number, genId: number, slideNumber: number, overlays: unknown[]) =>
    api.put<{ ok: boolean; overlays: unknown[] }>(`/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/overlays`, { overlays }),

  // Live editor · HTML preview + per-slide CSS overrides.
  // Both endpoints support AbortSignal so rapid slider drags and slide switches
  // can cancel stale requests without stepping on newer local state.
  renderSlideHtml: (
    strategyId: number,
    genId: number,
    slideNumber: number,
    signal?: AbortSignal,
  ) =>
    api.post<{ html: string; effective_overrides: SlideOverrides; supported_override_keys: string[] }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/render-html`,
      {},
      { signal },
    ),

  updateSlideOverrides: (
    strategyId: number,
    genId: number,
    slideNumber: number,
    overrides: SlideOverrides,
    signal?: AbortSignal,
  ) =>
    api.patch<{ ok: boolean; slide_overrides: SlideOverrides }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/overrides`,
      overrides,
      { signal },
    ),

  // Merge mode: only the keys in `partial` are written, others stay. Used by
  // per-key editors (inline text, overlay drag) so concurrent commits don't
  // race-overwrite each other. `seq` is a monotonic counter the server uses
  // to drop out-of-order updates.
  mergeSlideOverrides: (
    strategyId: number,
    genId: number,
    slideNumber: number,
    partial: SlideOverrides & { seq?: number },
    signal?: AbortSignal,
  ) =>
    api.patch<{ ok: boolean; slide_overrides: SlideOverrides }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/overrides?merge=true`,
      partial,
      { signal },
    ),

  suggestOverlayTexts: (strategyId: number, data: { slide_text: string; overlay_type?: string; content_lang?: string; count?: number }) =>
    api.post<{ suggestions: string[] }>(`/content-studio/${strategyId}/suggest-overlay-texts`, data),

  generateCtaVariants: (
    strategyId: number,
    genId: number,
    slideNumber: number,
    body?: { count?: number; content_lang?: string; preview?: boolean; prompt_override?: string },
  ) =>
    api.post<{
      phase?: 'seed' | 'grow' | 'convert';
      variants?: { category: string; label: string; text: string }[];
      preview?: boolean;
      prompt_system?: string;
      user_message?: string;
    }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/cta-variants`,
      body || {},
    ),

  // Admin · CTA Phase-Compliance Cleanup
  startCtaCleanup: (dryRun: boolean) =>
    api.post<{ job_id: string; dry_run: boolean; status: string }>(
      `/content-studio/admin/cta-cleanup?dry_run=${dryRun}`,
      {},
    ),

  getCtaCleanup: (jobId: string) =>
    api.get<{
      id: string;
      dry_run: boolean;
      status: 'pending' | 'running' | 'completed' | 'failed';
      scanned?: number;
      scanned_total?: number;
      debubbled?: number;
      rewrote?: number;
      skipped?: number;
      errors?: { gen_id: number; slide_number: number; err: string }[];
      violations?: {
        gen_id: number;
        slide_number: number;
        strategy_id: number;
        phase: string;
        violations: string[];
        old_text: string;
      }[];
      error?: string;
    }>(`/content-studio/admin/cta-cleanup/${jobId}`),

  suggestChartFromSlide: (
    strategyId: number,
    genId: number,
    slideNumber: number,
  ) =>
    api.post<{
      success: boolean;
      reason?: string;
      fallback?: string;
      chart_type?: string;
      data?: { label: string; value: number }[];
      suggested_style?: string;
    }>(`/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/suggest-chart`, {}),

  suggestDiagramFromSlide: (
    strategyId: number,
    genId: number,
    slideNumber: number,
    forcedType?: string,
  ) =>
    api.post<{
      success: boolean;
      reason?: string;
      fallback?: string;
      diagram_type?: string;
      style_preset?: string;
      items?: { label: string; description?: string; icon?: string }[];
      left_title?: string;
      right_title?: string;
      left_items?: { label: string; description?: string }[];
      right_items?: { label: string; description?: string }[];
      quote_text?: string;
      quote_author?: string;
      root_label?: string;
    }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/suggest-diagram`,
      forcedType ? { diagram_type: forcedType } : {},
    ),

  // ── Diagram-First Workflow (CONTENT_STUDIO_DIAGRAM_STEP_ENABLED) ───────────
  suggestDiagramVariants: (
    strategyId: number,
    genId: number,
    slideNumber: number,
    options?: { n?: number; allowed_types?: string[] },
  ) =>
    api.post<{
      variants: Array<{
        diagram_type: string;
        style_preset: string;
        rationale?: string;
        items?: Array<{ label: string; description?: string; icon?: string; image_url?: string | null }>;
        left_title?: string;
        right_title?: string;
        left_items?: Array<{ label: string; description?: string }>;
        right_items?: Array<{ label: string; description?: string }>;
        quote_text?: string;
        quote_author?: string;
        root_label?: string;
      }>;
      latency_ms: number;
    }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/suggest-diagram-variants`,
      options || {},
    ),

  acceptDiagram: (
    strategyId: number,
    genId: number,
    slideNumber: number,
    diagram: {
      diagram_type: string;
      style_preset?: string;
      items?: Array<{ label: string; description?: string; icon?: string; image_url?: string | null }>;
      left_title?: string;
      right_title?: string;
      left_items?: Array<{ label: string; description?: string }>;
      right_items?: Array<{ label: string; description?: string }>;
      quote_text?: string;
      quote_author?: string;
      root_label?: string;
    },
  ) =>
    api.post<{
      slide_number: number;
      slide_text: string;
      template_id: string;
      diagram: typeof diagram;
      original_text: string;
    }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/accept-diagram`,
      { diagram },
    ),

  resetDiagram: (strategyId: number, genId: number, slideNumber: number) =>
    api.post<{
      slide_number: number;
      slide_text?: string;
      template_id: string | null;
      diagram: null;
      noop?: boolean;
      reason?: string;
    }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/reset-diagram`,
      {},
    ),

  // ── Diagram v2 (production migration) ─────────────────────────────────
  suggestDiagramV2: (
    strategyId: number,
    genId: number,
    slideNumber: number,
    body: {
      user_text: string;
      forced_type?: 'flow' | 'hierarchy' | 'cycle' | null;
      image_mode?: 'replace' | 'icon-top';
    },
  ) =>
    api.post<import('../types').DiagramV2SuggestResponse>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/diagram-v2/suggest`,
      body,
    ),

  acceptDiagramV2: (
    strategyId: number,
    genId: number,
    slideNumber: number,
    diagram: import('../types').DiagramV2,
  ) =>
    api.post<{
      slide_number: number;
      slide_text: string;
      template_id: string;
      diagram: import('../types').DiagramV2;
      original_text: string;
    }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/accept-diagram`,
      { diagram },
    ),

  getDiagramAssetLibrary: () =>
    api.get<import('../types').DiagramAssetLibraryResponse>(
      `/content-studio/diagram-asset-library`,
    ),

  uploadDiagramImage: async (
    strategyId: number,
    genId: number,
    slideNumber: number,
    itemIndex: number,
    file: File,
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = localStorage.getItem('token') || '';
    const url = `/api/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/upload-diagram-image?item_index=${itemIndex}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Diagram image upload failed (${res.status}): ${txt.slice(0, 200)}`);
    }
    return res.json() as Promise<{
      image_url: string;
      filename: string;
      item_index: number;
      width: number;
      height: number;
    }>;
  },

  // Phase 4 · Generic per-slide image upload for ANY block image_url field.
  // Pass either `file` (multipart) OR `imageUrl` (external URL). The block_path
  // names the JSON path inside slide_data, e.g. "blocks[0].image_url" or
  // "blocks[2].cells[1].image_url" or legacy "diagram.items[0].image_url".
  uploadSlideImage: async (params: {
    strategyId: number;
    genId: number;
    slideNumber: number;
    blockPath: string;
    file?: File;
    imageUrl?: string;
  }) => {
    const { strategyId, genId, slideNumber, blockPath, file, imageUrl } = params;
    const token = localStorage.getItem('token') || '';
    const qs = new URLSearchParams({ block_path: blockPath });
    if (imageUrl) qs.set('image_url', imageUrl);
    const url = `/api/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/upload-image?${qs.toString()}`;
    const init: RequestInit = {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    };
    if (file) {
      const fd = new FormData();
      fd.append('file', file);
      init.body = fd;
    }
    const res = await fetch(url, init);
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Slide image upload failed (${res.status}): ${txt.slice(0, 200)}`);
    }
    return res.json() as Promise<{
      image_url: string;
      filename?: string;
      block_path: string;
      width?: number;
      height?: number;
      rerender_job_id: string | null;
    }>;
  },

  // Create or replace a phone-mockup block (iMessage / Tinder) on a slide.
  // Pass `blockPath: null` to append, or a path like "blocks[2]" to replace.
  // Block must be an ImageBlock or ImageTextSplitBlock with `mockup` set.
  upsertSlidePhoneMockupBlock: async (params: {
    strategyId: number;
    genId: number;
    slideNumber: number;
    blockPath: string | null;
    block: {
      type: 'image' | 'image_text_split';
      mockup: import('../types').PhoneMockupSpec;
      // Common optionals · only image_text_split uses headline/body/bullets/image_position/ratio.
      image_position?: 'left' | 'right';
      ratio?: '1:1' | '2:3' | '3:2' | '1:2' | '2:1';
      headline?: string;
      body?: string;
      caption?: string;
    };
  }) => {
    const { strategyId, genId, slideNumber, blockPath, block } = params;
    return api.post<{
      block_path: string;
      block: typeof block;
      rerender_job_id: string | null;
    }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/phone-mockup-block`,
      { block_path: blockPath, block },
    );
  },

  // Delete a top-level block (`blocks[N]`) from a slide. Nested paths are
  // rejected server-side with 400 · this is intentionally narrow so callers
  // cannot mutate block-internal structure via this endpoint.
  deleteSlideBlock: async (params: {
    strategyId: number;
    genId: number;
    slideNumber: number;
    blockPath: string;
  }) => {
    const { strategyId, genId, slideNumber, blockPath } = params;
    const qs = new URLSearchParams({ block_path: blockPath });
    return api.delete<{
      block_path: string;
      removed_type: string | null;
      rerender_job_id: string | null;
      rerender_error?: string;
    }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/blocks?${qs.toString()}`,
    );
  },

  // Phase 4 · Replace a DiagramBlock at block_path with a fully validated
  // new payload. The endpoint validates via DiagramBlock.model_validate; on
  // success persists, sets is_stale, and triggers an auto re-render.
  patchSlideDiagram: async (params: {
    strategyId: number;
    genId: number;
    slideNumber: number;
    blockPath: string;
    diagramBlock: {
      type: 'diagram';
      diagram_type: 'flow' | 'hierarchy' | 'cycle';
      style_preset?: string;
      direction?: 'LR' | 'TB';
      nodes: Array<{ id: string; label: string; description?: string; image?: { url: string; mode?: 'icon-top' | 'replace' } }>;
      edges: Array<{ from: string; to: string; label?: string }>;
    };
  }) => {
    const { strategyId, genId, slideNumber, blockPath, diagramBlock } = params;
    return api.patch<{
      block_path: string;
      diagram_block: typeof diagramBlock;
      rerender_job_id: string | null;
    }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/diagram`,
      { block_path: blockPath, diagram_block: diagramBlock },
    );
  },

  /**
   * Update size/aspect/fit overrides on a block image. Send `null` for any
   * field to clear that override (revert to template default). Omit a field
   * to leave it untouched.
   */
  setBlockImageSize: (params: {
    strategyId: number;
    genId: number;
    slideNumber: number;
    blockPath: string;
    imageMaxWidthPct?: number | null;
    imageAspect?: 'auto' | 'square' | '4:3' | '3:4' | '16:9' | '9:16' | null;
    imageFit?: 'cover' | 'contain' | null;
  }) => {
    const { strategyId, genId, slideNumber, blockPath, ...rest } = params;
    const body: Record<string, unknown> = { block_path: blockPath };
    if ('imageMaxWidthPct' in rest) body.image_max_width_pct = rest.imageMaxWidthPct;
    if ('imageAspect' in rest) body.image_aspect = rest.imageAspect;
    if ('imageFit' in rest) body.image_fit = rest.imageFit;
    return api.patch<{
      block_path: string;
      applied: Record<string, unknown>;
      block: {
        type?: string;
        image_max_width_pct?: number | null;
        image_aspect?: string | null;
        image_fit?: string | null;
      };
      rerender_job_id: string | null;
    }>(
      `/content-studio/${strategyId}/generations/${genId}/slides/${slideNumber}/block-image-size`,
      body,
    );
  },

  /**
   * List block-layout presets for a slide. Only available for composition
   * (`_tk2_composition*`) templates · non-composition slides receive
   * `available: false` so the picker is hidden.
   */
  getAvailableLayouts: (params: {
    strategyId: number;
    genId: number;
    slideNumber: number;
  }) =>
    api.get<{
      available: boolean;
      reason?: string;
      current_preset_id: string | null;
      current_index: number | null;
      seq?: number;
      presets: Array<{ id: string; label: string; description: string; slot_count: number }>;
    }>(
      `/content-studio/${params.strategyId}/generations/${params.genId}/slides/${params.slideNumber}/available-layouts`,
    ),

  /**
   * Apply a layout preset to a single slide. Preserves canonical text and
   * cached slot payloads (image URLs, diagrams, etc.) across switches.
   * `expectedSeq` enables optimistic locking · if it doesn't match the
   * server's current seq, returns 409 and the client should refetch.
   */
  applySlideLayout: (params: {
    strategyId: number;
    genId: number;
    slideNumber: number;
    presetId: string;
    expectedSeq?: number;
    signal?: AbortSignal;
  }) => {
    const body: Record<string, unknown> = { preset_id: params.presetId };
    if (params.expectedSeq != null) body.expected_seq = params.expectedSeq;
    return api.post<{
      preset_id: string;
      blocks: Array<Record<string, unknown>>;
      template_id: string;
      missing_slots: Array<{ slot_name: string; filler_type: string }>;
      rerender_job_id: string | null;
      seq: number;
    }>(
      `/content-studio/${params.strategyId}/generations/${params.genId}/slides/${params.slideNumber}/apply-layout`,
      body,
      { signal: params.signal },
    );
  },

  /** Phase 4 (warm-dijkstra) · Reorder blocks within a slide.
   *  `order` must be a permutation of [0..n-1] of the current block indices. */
  reorderSlideBlocks: (params: {
    strategyId: number;
    genId: number;
    slideNumber: number;
    order: number[];
    expectedSeq?: number;
  }) => {
    const body: Record<string, unknown> = { order: params.order };
    if (params.expectedSeq != null) body.expected_seq = params.expectedSeq;
    return api.post<{
      blocks: Array<Record<string, unknown>>;
      rerender_job_id: string | null;
      seq: number;
    }>(
      `/content-studio/${params.strategyId}/generations/${params.genId}/slides/${params.slideNumber}/reorder-blocks`,
      body,
    );
  },

  /** Edit the text of a single composition block (header / paragraph / cta) in
   *  place. For a cta block this sets user_edited=true server-side so a later
   *  slide rewrite won't clobber the manual button label. */
  updateSlideBlockText: (params: {
    strategyId: number;
    genId: number;
    slideNumber: number;
    blockIndex: number;
    text: string;
    expectedSeq?: number;
  }) => {
    const body: Record<string, unknown> = { text: params.text };
    if (params.expectedSeq != null) body.expected_seq = params.expectedSeq;
    return api.patch<{
      blocks: Array<Record<string, unknown>>;
      rerender_job_id: string | null;
      seq: number;
    }>(
      `/content-studio/${params.strategyId}/generations/${params.genId}/slides/${params.slideNumber}/blocks/${params.blockIndex}`,
      body,
    );
  },

  /** Phase 4 (warm-dijkstra) · AI suggests the best-fit layout for a slide.
   *  Returns one of the role-applicable preset ids + a structured rationale
   *  code from a closed vocabulary (no free-form reasoning). */
  suggestSlideLayout: (params: {
    strategyId: number;
    genId: number;
    slideNumber: number;
  }) =>
    api.post<{
      preset_id: string;
      rationale_code: string;
      cached?: boolean;
    }>(
      `/content-studio/${params.strategyId}/generations/${params.genId}/slides/${params.slideNumber}/suggest-layout`,
      {},
    ),

  /** Phase 3 (warm-dijkstra) · Patch the `overlay` style on every
   *  image_overlay_hook block in a slide. Merge-semantics; other fields
   *  (headline, image_url) untouched. */
  setImageOverlayStyle: (params: {
    strategyId: number;
    genId: number;
    slideNumber: number;
    overlay: string;
    textPosition?: 'top' | 'center' | 'bottom';
    aspect?: 'square' | 'portrait' | 'landscape' | 'wide' | 'full';
    expectedSeq?: number;
  }) => {
    const body: Record<string, unknown> = { overlay: params.overlay };
    if (params.textPosition) body.text_position = params.textPosition;
    if (params.aspect) body.aspect = params.aspect;
    if (params.expectedSeq != null) body.expected_seq = params.expectedSeq;
    return api.post<{
      overlay: string;
      text_position: string | null;
      aspect: string | null;
      patched: number;
      rerender_job_id: string | null;
      seq: number;
    }>(
      `/content-studio/${params.strategyId}/generations/${params.genId}/slides/${params.slideNumber}/image-overlay-style`,
      body,
    );
  },

  /** Phase 1 (warm-dijkstra) · Bulk-upgrade legacy/plain-text slides in a
   *  generation to their role-default block layout. Idempotent; backs up
   *  pre-upgrade state before any mutation. */
  bulkApplyDefaultLayout: (params: {
    strategyId: number;
    genId: number;
    roles?: Array<'hook' | 'body' | 'cta'>;
    dryRun?: boolean;
  }) => {
    const body: Record<string, unknown> = {};
    if (params.roles) body.roles = params.roles;
    if (params.dryRun) body.dry_run = true;
    return api.post<{
      summary: {
        eligible: number;
        upgraded?: number;
        would_upgrade?: number;
        skipped: number;
        cannot_upgrade: number;
        conflict: number;
        already_upgraded: number;
      };
      slides: Array<{
        slide_number: number;
        role: string;
        decision: string;
        target_preset?: string;
        canonical_preview?: Record<string, string>;
        extraction_confidence?: string;
        reason?: string;
      }>;
    }>(
      `/content-studio/${params.strategyId}/generations/${params.genId}/bulk-apply-default-layout`,
      body,
    );
  },

  /** Restore the most recent pre-upgrade slide_data from backups. */
  revertLayoutUpgrade: (params: {
    strategyId: number;
    genId: number;
    slideNumbers?: number[];
  }) => {
    const body: Record<string, unknown> = {};
    if (params.slideNumbers) body.slide_numbers = params.slideNumbers;
    return api.post<{
      reverted: Array<{ slide_number: number; status: string }>;
      count: number;
    }>(
      `/content-studio/${params.strategyId}/generations/${params.genId}/revert-layout-upgrade`,
      body,
    );
  },

  /** Save user-edited content for a structured slot (numbered_steps / list /
   *  faq / timeline / quote / stat_hero / comparison_cards). Validated
   *  server-side against the corresponding block schema. */
  saveSlotContent: (params: {
    strategyId: number;
    genId: number;
    slideNumber: number;
    presetId: string;
    slotName: string;
    content: Record<string, unknown>;
    expectedSeq?: number;
  }) => {
    const body: Record<string, unknown> = {
      preset_id: params.presetId,
      slot_name: params.slotName,
      content: params.content,
    };
    if (params.expectedSeq != null) body.expected_seq = params.expectedSeq;
    return api.post<{
      preset_id: string;
      slot_name: string;
      blocks: Array<Record<string, unknown>>;
      seq: number;
      rerender_job_id: string | null;
    }>(
      `/content-studio/${params.strategyId}/generations/${params.genId}/slides/${params.slideNumber}/slot-content`,
      body,
    );
  },

  /** Generate proposed content for an empty slot via Claude. Does NOT persist;
   *  caller previews and saves via `saveSlotContent`. */
  aiFillSlot: (params: {
    strategyId: number;
    genId: number;
    slideNumber: number;
    presetId: string;
    slotName: string;
    signal?: AbortSignal;
  }) =>
    api.post<{
      preset_id: string;
      slot_name: string;
      filler_type: string;
      content: Record<string, unknown> | Array<Record<string, unknown>>;
    }>(
      `/content-studio/${params.strategyId}/generations/${params.genId}/slides/${params.slideNumber}/slot-ai-fill`,
      { preset_id: params.presetId, slot_name: params.slotName },
      { signal: params.signal },
    ),

  listCustomTemplates: () =>
    api.get<{ items: CustomTemplate[] }>(`/custom-templates`),

  createCustomTemplate: (data: { name: string; base_template_id: string; overrides: SlideOverrides }) =>
    api.post<CustomTemplate>(`/custom-templates`, data),

  deleteCustomTemplate: (id: number) =>
    api.delete<{ ok: boolean }>(`/custom-templates/${id}`),

  searchStockPhotos: (query: string, options?: {
    source?: string; per_page?: number; page?: number; enrich?: boolean; niche?: string;
  }) => api.get<{ photos: StockPhoto[]; query_used: string; source_used: string; warnings: StockWarning[] }>(
    `/images/stock/search?${new URLSearchParams({
      query,
      source: options?.source || 'pexels',
      per_page: String(options?.per_page || 6),
      page: String(options?.page || 1),
      ...(options?.enrich ? { enrich: 'true' } : {}),
      ...(options?.niche ? { niche: options.niche } : {}),
    })}`
  ),

  generateAiImage: (body: {
    prompt: string; model?: string; size?: string; optimize_prompt?: boolean; niche?: string;
  }) => api.post<AiGeneratedImage>('/images/ai/generate', body),

  suggestStockAlternatives: (query: string, niche?: string) => {
    const q = new URLSearchParams({ query });
    if (niche) q.set('niche', niche);
    return api.get<{ alternatives: string[] }>(`/images/stock/alternatives?${q}`);
  },

  // Content Formats
  listContentFormats: (): Promise<{ formats: ContentFormatDef[] }> =>
    api.get<{ formats: ContentFormatDef[] }>('/content-studio/content-formats'),

  rankTemplates: (strategyId: number, text: string, contentFormat?: string): Promise<{ rankings: TemplateRanking[] }> =>
    api.post<{ rankings: TemplateRanking[] }>(`/content-studio/${strategyId}/rank-templates`, {
      text,
      content_format: contentFormat,
    }),

  // Content Plan
  getPlan: (strategyId: number, channelId?: number): Promise<{ plan: ContentPlan | null; slots: ContentPlanSlot[] }> => {
    const qs = channelId != null ? `?channel_id=${channelId}` : '';
    return api.get<{ plan: ContentPlan | null; slots: ContentPlanSlot[] }>(`/content-studio/${strategyId}/plan${qs}`);
  },

  createPlan: (strategyId: number, data: Partial<ContentPlan> & { cycle_type?: string; name?: string; channel_id?: number }): Promise<{ plan: ContentPlan; slots: ContentPlanSlot[] }> =>
    api.post<{ plan: ContentPlan; slots: ContentPlanSlot[] }>(`/content-studio/${strategyId}/plan`, data),

  updatePlan: (strategyId: number, planId: number, data: Partial<ContentPlan>): Promise<{ plan: ContentPlan }> =>
    api.put<{ plan: ContentPlan }>(`/content-studio/${strategyId}/plan/${planId}`, data),

  deletePlan: (strategyId: number, planId: number): Promise<{ ok: boolean }> =>
    api.delete<{ ok: boolean }>(`/content-studio/${strategyId}/plan/${planId}`),

  getTodayTimeline: (strategyId: number): Promise<{ slots: ContentPlanSlot[] }> =>
    api.get<{ slots: ContentPlanSlot[] }>(`/content-studio/${strategyId}/plan/today-timeline`),

  addSlot: (strategyId: number, planId: number, data: Partial<ContentPlanSlot>): Promise<{ slot_id: number; slots: ContentPlanSlot[] }> =>
    api.post<{ slot_id: number; slots: ContentPlanSlot[] }>(`/content-studio/${strategyId}/plan/${planId}/slots`, data),

  updateSlot: (strategyId: number, planId: number, slotId: number, data: Partial<ContentPlanSlot>): Promise<{ slots: ContentPlanSlot[] }> =>
    api.put<{ slots: ContentPlanSlot[] }>(`/content-studio/${strategyId}/plan/${planId}/slots/${slotId}`, data),

  deleteSlot: (strategyId: number, planId: number, slotId: number): Promise<{ slots: ContentPlanSlot[] }> =>
    api.delete<{ slots: ContentPlanSlot[] }>(`/content-studio/${strategyId}/plan/${planId}/slots/${slotId}`),

  revertSlot: (strategyId: number, planId: number, slotId: number): Promise<{ slots: ContentPlanSlot[] }> =>
    api.post<{ slots: ContentPlanSlot[] }>(`/content-studio/${strategyId}/plan/${planId}/slots/${slotId}/revert`, {}),

  getPlanChangelog: (strategyId: number, planId: number): Promise<{ changelog: PlanChangelog[] }> =>
    api.get<{ changelog: PlanChangelog[] }>(`/content-studio/${strategyId}/plan/${planId}/changelog`),

  // Format Performance
  getFormatPerformance: (strategyId: number): Promise<{ performance: FormatPerformance[]; suggestions: RuleSuggestion[] }> =>
    api.get<{ performance: FormatPerformance[]; suggestions: RuleSuggestion[] }>(`/content-studio/${strategyId}/format-performance`),

  // Critic Stats (Phase 2: Adversarial Critic Observability)
  getCriticStats: (strategyId: number, days = 30): Promise<CriticStats> =>
    api.get<CriticStats>(`/content-studio/${strategyId}/critic-stats?days=${days}`),

  getAIAnalysis: (strategyId: number): Promise<{ analysis: AIAnalysis }> =>
    api.get<{ analysis: AIAnalysis }>(`/content-studio/${strategyId}/ai-analysis`),

  runAutoAdjust: (strategyId: number): Promise<{ swaps: Array<{ slot_id: number; from: string; to: string }>; slots: ContentPlanSlot[] }> =>
    api.post<{ swaps: Array<{ slot_id: number; from: string; to: string }>; slots: ContentPlanSlot[] }>(`/content-studio/${strategyId}/auto-adjust`, {}),

  generateImage: async (
    strategyId: number,
    genId: number,
    data: { template_id: string; image_url?: string; image_url_left?: string; image_url_right?: string; logo_url?: string; language?: string; logo_size?: number; logo_border_width?: number; logo_border_color?: string; source_ref?: string; aspect_ratio?: string; variant_text?: string }
  ): Promise<Blob> => {
    const res = await fetchWithAuth(
      `/api/content-studio/${strategyId}/generations/${genId}/generate-image`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return res.blob();
  },

  generateMultiPlatform: (strategyId: number, data: MultiPlatformGenerationRequest) =>
    api.post<{ generations: ContentGeneration[] }>(
      `/content-studio/${strategyId}/generate-multi-platform`,
      data
    ),

  suggestBatchTopics: (strategyId: number, data?: { n?: number; content_lang?: string }) =>
    api.post<{ topics: BatchTopic[] }>(
      `/content-studio/${strategyId}/batch/suggest-topics`,
      data ?? {}
    ),

  batchGenerate: (
    strategyId: number,
    data: { topics: BatchTopic[]; model?: string; competitor_post_ids?: number[] }
  ) =>
    api.post<{ generations: ContentGeneration[]; errors: string[] }>(
      `/content-studio/${strategyId}/batch/generate`,
      data
    ),

  // ----- Auto-Pilot Library (book-scoped, cross-account) -------------------
  autoPilotLibraryBooks: (strategyId: number) =>
    api.get<{ books: AutoPilotLibraryBook[] }>(
      `/content-studio/library/books?strategy_id=${strategyId}`
    ),

  autoPilotLibraryExtractPillars: (
    documentId: number,
    params: { lang: string; strategy_id?: number }
  ) => {
    const q = new URLSearchParams({ lang: params.lang });
    if (params.strategy_id != null) q.set('strategy_id', String(params.strategy_id));
    return api.post<{ status: string; run_id: string; lang: string }>(
      `/content-studio/library/books/${documentId}/pillars/extract?${q.toString()}`,
      {}
    );
  },

  autoPilotLibraryListPillars: (documentId: number) =>
    api.get<{
      pillars: Array<{
        id: number; name: string; description: string; sort_order: number;
        document_id: number | null; insight_count: number; language?: string;
      }>;
    }>(`/content-studio/library/books/${documentId}/pillars`),

  autoPilotLibrarySynthesizeTopics: (
    documentId: number,
    params: { strategy_id: number; lang: string }
  ) => {
    const q = new URLSearchParams({
      strategy_id: String(params.strategy_id),
      lang: params.lang,
    });
    return api.post<{ status: string; run_id: string; lang: string }>(
      `/content-studio/library/books/${documentId}/topic-clusters/synthesize?${q.toString()}`,
      {}
    );
  },

  autoPilotLibraryListTopicClusters: (
    documentId: number,
    params: { strategy_id: number; status?: string }
  ) => {
    const q = new URLSearchParams({ strategy_id: String(params.strategy_id) });
    if (params.status) q.set('status', params.status);
    return api.get<{ clusters: AutoPilotTopicCluster[] }>(
      `/content-studio/library/books/${documentId}/topic-clusters?${q.toString()}`
    );
  },

  autoPilotLibrarySynthesisStatus: (
    documentId: number,
    params: { strategy_id: number; kind: 'pillar_extract' | 'topic_synth' }
  ) => {
    const q = new URLSearchParams({
      strategy_id: String(params.strategy_id),
      kind: params.kind,
    });
    return api.get<{ job: AutoPilotSynthJob | null }>(
      `/content-studio/library/books/${documentId}/synthesis-status?${q.toString()}`
    );
  },

  // ----- Auto-Pilot (Pillars + Topic-Clusters) -----------------------------
  autoPilotExtractPillars: (strategyId: number, documentId?: number) =>
    api.post<{ status: string; run_id: string }>(
      `/content-studio/${strategyId}/pillars/extract${documentId ? `?document_id=${documentId}` : ''}`,
      {}
    ),

  autoPilotListPillars: (strategyId: number, documentId?: number) =>
    api.get<{
      pillars: Array<{
        id: number; name: string; description: string; sort_order: number;
        document_id: number | null; insight_count: number;
      }>;
      latest_job: AutoPilotSynthJob | null;
    }>(
      `/content-studio/${strategyId}/pillars${documentId ? `?document_id=${documentId}` : ''}`
    ),

  autoPilotSynthesizeTopics: (strategyId: number, documentId?: number) =>
    api.post<{ status: string; run_id: string }>(
      `/content-studio/${strategyId}/topic-clusters/synthesize${documentId ? `?document_id=${documentId}` : ''}`,
      {}
    ),

  autoPilotListTopicClusters: (
    strategyId: number,
    params?: { pillar_id?: number; status?: string; limit?: number }
  ) => {
    const q = new URLSearchParams();
    if (params?.pillar_id != null) q.set('pillar_id', String(params.pillar_id));
    if (params?.status) q.set('status', params.status);
    if (params?.limit != null) q.set('limit', String(params.limit));
    const qs = q.toString();
    return api.get<{ clusters: AutoPilotTopicCluster[] }>(
      `/content-studio/${strategyId}/topic-clusters${qs ? `?${qs}` : ''}`
    );
  },

  autoPilotSynthesisStatus: (strategyId: number, runId?: string, kind?: 'pillar_extract' | 'topic_synth') => {
    const q = new URLSearchParams();
    if (runId) q.set('run_id', runId);
    if (kind) q.set('kind', kind);
    return api.get<{ job: AutoPilotSynthJob | null }>(
      `/content-studio/${strategyId}/topic-clusters/synthesis-status?${q.toString()}`
    );
  },

  autoPilotApproveCluster: (strategyId: number, clusterId: number) =>
    api.post<{ status: string }>(
      `/content-studio/${strategyId}/topic-clusters/${clusterId}/approve`, {}
    ),

  autoPilotRejectCluster: (strategyId: number, clusterId: number, reason?: string) =>
    api.post<{ status: string }>(
      `/content-studio/${strategyId}/topic-clusters/${clusterId}/reject`,
      reason ? { reason } : {}
    ),

  // Experiment endpoints
  getNextExperiment: (strategyId: number, contentType: string): Promise<ExperimentSuggestion> =>
    api.get<ExperimentSuggestion>(
      `/content-studio/${strategyId}/next-experiment?content_type=${contentType}`
    ),

  getExperimentMatrix: (strategyId: number, contentType?: string): Promise<ExperimentMatrix> => {
    const q = contentType ? `?content_type=${contentType}` : '';
    return api.get<ExperimentMatrix>(`/content-studio/${strategyId}/experiment-matrix${q}`);
  },

  getExperimentWinners: (strategyId: number): Promise<WinningPatterns | null> =>
    api.get<WinningPatterns | null>(`/content-studio/${strategyId}/experiment-matrix/winners`),

  getNicheInsights: (strategyId: number): Promise<NicheInsights | null> =>
    api.get<NicheInsights | null>(`/content-studio/${strategyId}/experiment-matrix/niche-insights`),

  getExperiments: (
    strategyId: number,
    params?: { content_type?: string; is_manual?: number; limit?: number; offset?: number }
  ): Promise<{ items: ContentExperiment[]; total: number }> => {
    const q = new URLSearchParams();
    if (params?.content_type) q.set('content_type', params.content_type);
    if (params?.is_manual !== undefined) q.set('is_manual', String(params.is_manual));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    const qs = q.toString() ? `?${q.toString()}` : '';
    return api.get<{ items: ContentExperiment[]; total: number }>(
      `/content-studio/${strategyId}/experiments${qs}`
    );
  },

  // Topic Pool
  importTopicPool: (strategyId: number): Promise<{ inserted: number; skipped: number; pool_stats: PoolStats }> =>
    api.post<{ inserted: number; skipped: number; pool_stats: PoolStats }>(
      `/content-studio/${strategyId}/topic-pool/import`,
      {}
    ),

  getPoolStats: (strategyId: number): Promise<PoolStats> =>
    api.get<PoolStats>(`/content-studio/${strategyId}/topic-pool/stats`),

  resetFailedTopics: (strategyId: number): Promise<{ reset: number; pool_stats: PoolStats }> =>
    api.post<{ reset: number; pool_stats: PoolStats }>(
      `/content-studio/${strategyId}/topic-pool/reset-failed`,
      {}
    ),

  // Week Plan
  getCurrentWeek: (strategyId: number, params: { channel_id?: number }) => {
    const q = new URLSearchParams();
    if (params.channel_id != null) q.set('channel_id', String(params.channel_id));
    const qs = q.toString() ? `?${q.toString()}` : '';
    return api.get<{ suggestions: WeekSlotSuggestion[]; generations: (WeekSlotGeneration & { day?: string; time_window?: string; content_format?: string })[]; week_monday?: string; time_window_map?: Record<string, string> }>(`/content-studio/${strategyId}/week-plan/current${qs}`);
  },

  resetWeekPlan: (strategyId: number, params: { channel_id?: number }) =>
    api.post<{ deleted: number; preserved: number }>(`/content-studio/${strategyId}/week-plan/reset`, params),

  suggestWeekPlan: (strategyId: number, params: { channel_id?: number; content_lang?: string; theme?: string }) =>
    api.post<{ suggestions: WeekSlotSuggestion[]; theme?: string | null }>(`/content-studio/${strategyId}/week-plan/suggest`, params),

  generateWeekPlan: (strategyId: number, params: { channel_id?: number; content_lang?: string; confirmed_slots: WeekSlotSuggestion[] }) =>
    api.post<{ results: WeekSlotGeneration[] }>(`/content-studio/${strategyId}/week-plan/generate`, params),

  confirmWeekPlan: (strategyId: number, params: { channel_id?: number; generation_ids: number[] }) =>
    api.post<{ scheduled: Array<WeekPlanConfirmResult | { generation_id: number; error: string; message?: string }> }>(`/content-studio/${strategyId}/week-plan/confirm`, params),

  regenerateWeekSlot: (strategyId: number, params: { channel_id?: number; slot_id: number; overrides?: { topic_angle?: string; tension_category?: string; hook_formula?: string }; rejection_reason?: string; rejection_presets?: string[] }) =>
    api.post<{ result: WeekSlotGeneration }>(`/content-studio/${strategyId}/week-plan/regenerate-slot`, params),

  swapTemplate: (strategyId: number, params: { channel_id: number; generation_id: number; template_id?: string }) =>
    api.post<WeekSlotGeneration>(`/content-studio/${strategyId}/week-plan/swap-template`, params),

  cancelWeekPlanSchedule: (strategyId: number, publishLogId: number) =>
    api.delete<{ status: string }>(`/content-studio/${strategyId}/week-plan/publish-log/${publishLogId}`),

  triggerRerender: (
    strategyId: number,
    genId: number,
    opts?: { reassignTemplates?: boolean },
  ): Promise<{ job_id: number; status: string; reassigned?: boolean }> => {
    const qs = opts?.reassignTemplates ? '?reassign_templates=true' : '';
    return api.post<{ job_id: number; status: string; reassigned?: boolean }>(
      `/content-studio/${strategyId}/generations/${genId}/re-render-all${qs}`,
      {},
    );
  },

  // Engine config
  getEngineConfig: (strategyId: number, channelId?: number) =>
    api.get<EngineConfig>(`/content-studio/${strategyId}/engine-config${channelId ? `?channel_id=${channelId}` : ''}`),

  getKnowledgePanel: (strategyId: number) =>
    api.get<KnowledgePanel>(`/content-studio/${strategyId}/knowledge-panel`),

  // Cockpit scoring
  getCockpitScores: (strategyId: number, form: {
    content_type?: string;
    content_lang?: string;
    tension_category?: string | null;
    hook_formula?: string | null;
    experiments?: string[];
    topic_angle?: string;
    custom_rules?: string;
    preset_id?: string | null;
    model?: string;
    competitor_count?: number;
    document_ids?: number[];
  }, channelId?: number) =>
    api.post<CockpitScores>(
      `/content-studio/${strategyId}/engine-config/score${channelId ? `?channel_id=${channelId}` : ''}`,
      form,
    ),

  // Course Ideas (top-5 curriculum suggestions)
  getCourseIdeas: (strategyId: number, topicAngle?: string, tensionCategory?: string) =>
    api.get<{ ideas: CourseIdea[] }>(
      `/content-studio/${strategyId}/course-ideas?topic_angle=${encodeURIComponent(topicAngle || '')}&tension_category=${encodeURIComponent(tensionCategory || '')}`,
    ),

  // Course Lessons (ALL lessons · browse view with phase filter + search)
  getCourseLessons: (strategyId: number, topicAngle?: string, tensionCategory?: string) =>
    api.get<{ lessons: CourseLesson[] }>(
      `/content-studio/${strategyId}/course-lessons?topic_angle=${encodeURIComponent(topicAngle || '')}&tension_category=${encodeURIComponent(tensionCategory || '')}`,
    ),

  // Insights for a specific lesson (expand-on-select)
  getCourseLessonInsights: (strategyId: number, slug: string) =>
    api.get<{ insights: CourseLessonInsight[] }>(
      `/content-studio/${strategyId}/course-lessons/${encodeURIComponent(slug)}/insights`,
    ),

  // Re-extract insights for one lesson (Haiku, synchronous, rate-limited)
  reExtractLessonInsights: (strategyId: number, slug: string) =>
    api.post<ReExtractResponse>(
      `/content-studio/${strategyId}/lessons/${encodeURIComponent(slug)}/re-extract-insights`,
      {},
    ),

  // Progression cursor: next unused (lesson, insight) for this strategy
  getNextProgression: (strategyId: number) =>
    api.get<{ next: ProgressionNext | null }>(
      `/content-studio/${strategyId}/lessons/next-progression`,
    ),

  // Remove a single progression entry (so the user can revisit it)
  deleteProgressionEntry: (strategyId: number, slug: string, insightId: string) =>
    api.delete<{ ok: boolean }>(
      `/content-studio/${strategyId}/lessons/progression/${encodeURIComponent(slug)}/${encodeURIComponent(insightId)}`,
    ),

  // Quick Preview (single slide, supports multi-slide stepper)
  quickPreview: (strategyId: number, data: {
    content_type?: string;
    content_lang?: string;
    tension_category?: string | null;
    hook_formula?: string | null;
    experiments?: string[];
    topic_angle?: string;
    custom_rules?: string;
    aggressiveness?: number;
    aspect_focus?: string;
    tone_formality?: number;
    mehrwert?: number;
    selected_course_slug?: string | null;
    slide_position?: number;
    total_slides?: number;
    previous_slides?: PreviousSlide[];
    chosen_direction?: string;
  }, channelId?: number) =>
    api.post<QuickPreviewResponse>(
      `/content-studio/${strategyId}/quick-preview${channelId ? `?channel_id=${channelId}` : ''}`,
      data,
    ),

  // Carousel Blueprint (template allocation planner)
  carouselBlueprint: (strategyId: number, data: {
    content_lang?: string;
    tension_category?: string | null;
    topic_angle?: string;
    total_slides?: number;
    aggressiveness?: number;
    aspect_focus?: string;
    tone_formality?: number;
    mehrwert?: number;
  }, channelId?: number) =>
    api.post<BlueprintResponse>(
      `/content-studio/${strategyId}/carousel-blueprint${channelId ? `?channel_id=${channelId}` : ''}`,
      data,
    ),

  // Quick Preview Options (3 direction proposals for next slide)
  quickPreviewOptions: (strategyId: number, data: {
    content_type?: string;
    content_lang?: string;
    tension_category?: string | null;
    topic_angle?: string;
    slide_position?: number;
    total_slides?: number;
    previous_slides?: PreviousSlide[];
  }, channelId?: number) =>
    api.post<{ options: DirectionOption[]; slide_position: number }>(
      `/content-studio/${strategyId}/quick-preview-options${channelId ? `?channel_id=${channelId}` : ''}`,
      data,
    ),

  // Slide Feedback (Like/Dislike Hypothesis System)
  submitSlideFeedback: (strategyId: number, data: SlideFeedbackPayload, channelId?: number) =>
    api.post<{ id: number; niche: string }>(
      `/content-studio/${strategyId}/slide-feedback${channelId ? `?channel_id=${channelId}` : ''}`,
      data,
    ),

  // Taste Profile (niche preference aggregation)
  getTasteProfile: (strategyId: number, channelId?: number) =>
    api.get<TasteProfile>(
      `/content-studio/${strategyId}/taste-profile${channelId ? `?channel_id=${channelId}` : ''}`,
    ),

  // AI Rewrite
  aiRewrite: (strategyId: number, data: {
    target: string;
    original_text: string;
    adjustment: string;
    custom_instruction?: string;
    niche?: string;
    lang?: string;
  }) =>
    api.post<{ rewritten: string; adjustment: string; adjustment_label: string; reasoning?: string; changes?: string; impact?: string }>(
      `/content-studio/${strategyId}/ai-rewrite`,
      data,
    ),

  // Strategy field patch (sync rewrite back to Strategy OS)
  patchStrategyField: (strategyId: number, field: string, value: string) =>
    api.patch<{ ok: boolean }>(`/content-studio/${strategyId}/strategy-field`, { field, value }),

  // Niche directive save · writes per-strategy override (DB), not the shared niche file
  patchNicheDirective: (strategyId: number, content: string) =>
    api.patch<{ ok: boolean; source: 'override' }>(
      `/content-studio/${strategyId}/niche-directive`,
      { content },
    ),

  // Reset per-strategy override → falls back to shared niche file
  resetNicheDirective: (strategyId: number) =>
    api.delete<{ ok: boolean; source: 'niche' }>(
      `/content-studio/${strategyId}/niche-directive`,
    ),

  // Generate niche directive from Strategy OS data → saved as per-strategy override
  generateNicheDirective: (
    strategyId: number,
    opts?: { preview?: boolean; prompt_override?: string; prompt_system_override?: string; language?: string },
  ) =>
    api.post<{ ok?: boolean; niche?: string; content?: string; source?: 'override'; preview?: boolean; prompt_system?: string; user_message?: string }>(
      `/content-studio/${strategyId}/generate-niche-directive`,
      opts ?? {},
    ),

  // Next free slots
  getNextFreeSlots: (strategyId: number, channelId: number, limit?: number) => {
    const q = new URLSearchParams({ channel_id: String(channelId) });
    if (limit) q.set('limit', String(limit));
    return api.get<{ slots: FreeSlot[]; no_plan?: boolean }>(`/content-studio/${strategyId}/next-free-slots?${q}`);
  },

  // Cockpit locks
  getCockpitLocks: (strategyId: number) =>
    api.get<Record<string, { value: string; locked_at: string; hard: boolean }>>(
      `/content-studio/${strategyId}/cockpit-locks`,
    ),
  setCockpitLock: (strategyId: number, field: string, value: string, hard?: boolean) =>
    api.put<{ value: string; locked_at: string; hard: boolean }>(
      `/content-studio/${strategyId}/cockpit-locks/${encodeURIComponent(field)}`,
      hard !== undefined ? { value, hard } : { value },
    ),
  clearCockpitLock: (strategyId: number, field: string) =>
    api.delete<{ removed: boolean }>(
      `/content-studio/${strategyId}/cockpit-locks/${encodeURIComponent(field)}`,
    ),

  // Viral hooks
  getViralHookCategories: (strategyId: number) =>
    api.get<Array<{ slug: string; label: string; count: number }>>(
      `/content-studio/${strategyId}/viral-hooks/categories`,
    ),
  getViralHooks: (
    strategyId: number,
    opts?: { category?: string; q?: string; bucket?: 'all' | 'top' | 'cold' | 'recent'; limit?: number },
  ) => {
    const params = new URLSearchParams();
    if (opts?.category) params.set('category', opts.category);
    if (opts?.q) params.set('q', opts.q);
    if (opts?.bucket && opts.bucket !== 'all') params.set('bucket', opts.bucket);
    if (opts?.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return api.get<any[]>(`/content-studio/${strategyId}/viral-hooks${qs ? `?${qs}` : ''}`);
  },

  // Slide-by-slide wizard
  wizardHookOptions: (
    strategyId: number,
    body: { user_idea: string; num_options: number; category?: string; content_lang?: string; topic_pool_id?: number; included_book_ids?: number[]; excluded_book_ids?: number[]; manual_insight_override?: string; slide_count?: number; source_generation_id?: number; preview?: boolean; prompt_override?: string; prompt_system_override?: string },
  ) => api.post<any>(`/content-studio/${strategyId}/wizard/hook-options`, body),

  wizardBodyOptions: (
    strategyId: number,
    body: {
      mehrwert_idea: string;
      num_options: number;
      slide_index: number;
      total_slides: number;
      previous_slides: Array<{ index: number; role: string; text: string }>;
      content_lang?: string;
      topic_pool_id?: number;
      auto_match_token?: string;
      included_book_ids?: number[];
      excluded_book_ids?: number[];
      manual_insight_override?: string;
      value_anchor?: ValueAnchor | null;
      preview?: boolean;
      prompt_override?: string;
      prompt_system_override?: string;
    },
    /** When true, server returns untouched substantive blocks + needs_human_decision
     * warnings so the frontend can prompt the user (split / shrink-with-loss / edit).
     * When false (default) the server falls back to lossy SHRINK with warn-severity
     * for old-client compatibility. */
    supportsHumanDecision: boolean = false,
  ) => api.post<any>(
    `/content-studio/${strategyId}/wizard/body-options${
      supportsHumanDecision ? '?supports_human_decision=true' : ''
    }`,
    body,
  ),

  wizardCtaOptions: (
    strategyId: number,
    body: {
      cta_idea?: string;
      num_options: number;
      total_slides: number;
      previous_slides: Array<{ index: number; role: string; text: string }>;
      content_lang?: string;
      auto_match_token?: string;
      preview?: boolean;
      prompt_override?: string;
      prompt_system_override?: string;
    },
  ) => api.post<any>(`/content-studio/${strategyId}/wizard/cta-options`, body),

  // Auto-Insight: synthetic generation + slide rendering + structured listing
  generateSyntheticInsight: (
    strategyId: number,
    body: {
      topic_angle: string;
      description: string;
      artifact_type: string;
      content_lang?: string;
      idempotency_key?: string;
    },
  ) =>
    api.post<{
      insight: { id: number; title: string; insight_text: string; tags: string[]; origin: string };
      auto_match_token: string;
      match_summary: string;
      rate_limit_remaining: number;
    }>(`/content-studio/${strategyId}/insights/generate-synthetic`, body),

  renderInsightAsSlide: (
    strategyId: number,
    insightId: number,
    body: { max_chars?: number; content_lang?: string },
  ) =>
    api.post<{ slide_text: string; content_type: string }>(
      `/content-studio/${strategyId}/insights/${insightId}/render-as-slide`,
      body,
    ),

  listStructuredInsights: (strategyId: number) =>
    api.get<{
      insights: Array<{
        id: number;
        title: string;
        cluster: string;
        tags: string[];
        chapter_label: string | null;
        origin: 'extracted' | 'ai_generated' | 'user_added';
      }>;
    }>(`/content-studio/${strategyId}/insights/structured`),

  stageAQualityCheck: (
    strategyId: number,
    body: {
      slides: Array<{ text: string; role?: string; content_type?: string }>;
      content_lang?: string;
      value_anchor?: ValueAnchor | null;
    },
  ) =>
    api.post<{
      has_concrete_value: boolean;
      confidence?: number;
      trigger?: string;
      missing_artifact_type: string | null;
      ideal_description: string | null;
      suggested_insights: Array<{
        id: number;
        title: string;
        summary: string;
        origin: string;
      }>;
      // Stage 0 · Drift-Felder (nur gesetzt wenn value_anchor uebergeben wurde)
      anchor_drift?: boolean;
      drift_meta?: {
        present: boolean;
        confidence: number;
        reason: string;
        target_slide_index: number | null;
      } | null;
    }>(`/content-studio/${strategyId}/stage-a-quality-check`, body),

  wizardFinalize: (
    strategyId: number,
    body: {
      slides: Array<{ text: string; role: string; content_type?: string }>;
      selected_hook_id?: string;
      content_lang?: string;
      channel_id: number;
      topic_pool_id?: number;
      defer_templates?: boolean;
      value_anchor?: ValueAnchor | null;
    },
  ) => api.post<any>(`/content-studio/${strategyId}/wizard/finalize`, body),

  // Stage 0 · Mehrwert-Gate: Anker-Kandidaten aus Insight-Pool extrahieren.
  fetchValueAnchors: (
    strategyId: number,
    body: { topic_pool_id: number; content_lang?: string },
  ) =>
    api.post<{
      anchors: ValueAnchorWithReuse[];
      allow_manual: boolean;
      ideal_description: string | null;
      trigger:
        | 'ok'
        | 'no_book_context'
        | 'haiku_timeout'
        | 'haiku_malformed'
        | 'no_anchors';
      // Phase 4: bei Course-Pools mit Transcript-Fallback wird ein lazy
      // re-extract Job angestossen. Frontend kann via getExtractionJob pollen.
      extraction_hint?: 'course_transcript' | 'course_insight' | null;
      course_lesson_slug?: string | null;
      extraction_job_id?: number;
      extraction_job_status?: 'queued' | 'running' | 'done' | 'failed';
    }>(`/content-studio/${strategyId}/wizard/value-anchors`, body),

  getExtractionJob: (strategyId: number, jobId: number) =>
    api.get<{
      id: number;
      lesson_slug: string;
      status: 'queued' | 'running' | 'done' | 'failed';
      insight_count_after: number | null;
      error: string | null;
      created_at: string;
      started_at: string | null;
      finished_at: string | null;
    }>(`/content-studio/${strategyId}/extraction-jobs/${jobId}`),

  // iter3 Stage A → Stage B: persist user-reviewed text + assign templates atomically.
  finalizeText: (
    strategyId: number,
    genId: number,
    slides: Array<{ text: string; role: string; content_type?: string }>,
  ) =>
    api.post<any>(
      `/content-studio/${strategyId}/generations/${genId}/finalize-text`,
      { slides },
    ),

  getTopicSuggestions: (strategyId: number, limit = 200) =>
    api.get<{
      suggestions: Array<{
        pool_id: number;
        topic_angle: string;
        chapter_label: string | null;
        chapter_index: number | null;
        document_id: number | null;
        document_name: string | null;
        source_insight_id: number | null;
        cluster: string | null;
        quality_score: number;
        created_at: string;
        pillar_id: number | null;
        pillar_name: string | null;
        pillar_sort: number | null;
        // Course-lesson cards (source='course_lesson') populate these:
        source: 'document_insight' | 'course_lesson';
        lesson_slug: string | null;
        lesson_title: string | null;
        // Phase 3: 1 = placeholder for a lesson that has no extracted
        // insights yet; Stage-0 anchor extractor falls back to transcript.
        synthetic?: number;
      }>;
    }>(`/content-studio/${strategyId}/topic-suggestions?limit=${limit}`),

  syncTopicSuggestions: (strategyId: number, accountId?: number) =>
    api.post<{ status: string; reclassify_queued: number }>(
      `/content-studio/${strategyId}/topic-suggestions/sync${accountId ? `?account_id=${accountId}` : ''}`,
      {},
    ),

  getTopicSyncStatus: (strategyId: number) =>
    api.get<{
      status: 'idle' | 'running' | 'done' | 'failed';
      job_type?: 'sync' | 'reclassify';
      started_at?: string;
      finished_at?: string;
      error?: string | null;
      inserted?: number;
      updated?: number;
      skipped?: number;
      considered?: number;
      reclassified?: number;
    }>(`/content-studio/${strategyId}/topic-suggestions/sync-status`),

  reExtractTopicSuggestions: (strategyId: number, accountId?: number) =>
    api.post<{ status: string }>(
      `/content-studio/${strategyId}/topic-suggestions/re-extract${accountId ? `?account_id=${accountId}` : ''}`,
      {},
    ),

  // Layer-12 active sources (persistent per strategy). Empty list = no filter
  // (legacy behavior: all pool sources are eligible). Non-empty = autopilot
  // and topic suggestions filtered to these sources, in array order.
  getLayer12Sources: (strategyId: number) =>
    api.get<{ sources: Array<{ type: 'book' | 'lesson'; id: string | number }> }>(
      `/content-studio/${strategyId}/layer12-sources`,
    ),

  updateLayer12Sources: (
    strategyId: number,
    sources: Array<{ type: 'book' | 'lesson'; id: string | number }>,
  ) =>
    api.patch<{ sources: Array<{ type: 'book' | 'lesson'; id: string | number }> }>(
      `/content-studio/${strategyId}/layer12-sources`,
      { sources: sources.map(s => ({ type: s.type, id: String(s.id) })) },
    ),

  getNextPoolTopic: (strategyId: number) =>
    api.get<{
      topic: {
        id: number;
        topic_angle: string;
        chapter_label: string | null;
        chapter_index: number | null;
        document_id: number | null;
        source: 'document_insight' | 'course_lesson';
        lesson_slug: string | null;
        lesson_title: string | null;
        pillar_sort: number | null;
        tension_category: string | null;
        hook_formula: string | null;
        quality_score: number;
      } | null;
      exhausted: boolean;
      reason: 'ok' | 'no_active_sources' | 'all_exhausted';
      active_sources_count: number;
    }>(`/content-studio/${strategyId}/pool/next`),

  visualFinalize: (strategyId: number, genId: number) =>
    api.post<{
      generation: any;
      plan_slot_id: number | null;
      slot_label: string | null;
      scheduled_at: string | null;
    }>(`/content-studio/${strategyId}/generations/${genId}/visual-finalize`, {}),

  // Iter3b Stage A → B: persist text only, no template assignment yet
  saveText: (
    strategyId: number,
    genId: number,
    slides: Array<{ text: string; role: string; content_type?: string }>,
  ) =>
    api.post<{ generation: any }>(
      `/content-studio/${strategyId}/generations/${genId}/save-text`,
      { slides },
    ),

  // Iter3b Stage B → C: assign templates + render + slot
  renderGeneration: (
    strategyId: number,
    genId: number,
    overrides?: {
      template_family?: string | null;
      photo_mode?: string | null;
      photo_style?: string | null;
      // 'none' explicitly disables a photo on the hook even if body photo_mode would otherwise enable it;
      // 'pool' switches the hook template to its photo variant (e.g. tkd_hook → tkd_photo_hook).
      hook_photo_mode?: 'none' | 'pool' | null;
      // Per-body-slide layout opt-ins. Slides not listed default to plain
      // (no composition AI call). Hook and CTA slides are ignored if listed.
      slide_layout_hints?: Array<{ slide_index: number; layout_kind: 'plain' | 'stats' | 'compare' | 'checklist' | 'quote' }>;
      save_as_default?: boolean;
    },
  ) =>
    api.post<{
      generation: any;
      plan_slot_id: number | null;
      slot_label: string | null;
      scheduled_at: string | null;
    }>(
      `/content-studio/${strategyId}/generations/${genId}/render`,
      overrides ?? {},
    ),

  // Resume-Banner: active text-review / layer-config drafts (includes slides for resume)
  getActiveDrafts: (strategyId: number) =>
    api.get<{
      drafts: Array<{
        id: number;
        pipeline_stage: string;
        created_at: string;
        slides: Array<{ number: number; text: string; content_type: string }>;
      }>;
    }>(
      `/content-studio/${strategyId}/generations/active-drafts`,
    ),

  setGenerationTemplate: (strategyId: number, genId: number, templateId: string, overrideMode?: string) =>
    api.patch<{ ok: boolean; template_id: string }>(
      `/content-studio/${strategyId}/generations/${genId}/template`,
      overrideMode ? { template_id: templateId, override_mode: overrideMode } : { template_id: templateId },
    ),

  // Photo pool
  listPoolPhotos: (strategyId: number, limit = 100, offset = 0) =>
    api.get<{ photos: Array<{ id: number; strategy_id: number; filename: string; width: number; height: number; usage_count: number }>; total: number }>(
      `/photo-pool/${strategyId}?limit=${limit}&offset=${offset}`,
    ),

  assignPhotoToSlide: (genId: number, slideNumber: number, photoId: number) =>
    api.put<{ ok: boolean; photo_id: number }>(
      `/photo-pool/carousel-slides/${genId}/${slideNumber}/photo`,
      { photo_id: photoId },
    ),

  uploadPoolPhotos: async (strategyId: number, files: File[]) => {
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    const token = localStorage.getItem('token') || '';
    const res = await fetch(`/api/photo-pool/${strategyId}/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) throw new Error(`Upload failed (${res.status})`);
    return res.json() as Promise<{ uploaded: Array<{ id: number; filename: string }>; errors: Array<{ filename: string; error: string }> }>;
  },

  uploadDiagramIcon: async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = localStorage.getItem('token') || '';
    const res = await fetch('/api/content-studio/diagram-icons/upload', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Upload failed (${res.status}): ${txt.slice(0, 200)}`);
    }
    return res.json() as Promise<{
      asset_id: string;
      icon: string;
      preview_url: string;
      width: number;
      height: number;
    }>;
  },

  listDiagramUploads: () =>
    api.get<{
      uploads: Array<{
        asset_id: string;
        icon: string;
        preview_url: string;
        width: number | null;
        height: number | null;
        created_at: string;
      }>;
    }>('/content-studio/diagram-icons/uploads'),

  deleteDiagramUpload: (assetId: string) =>
    api.delete<{ ok: boolean }>(`/content-studio/diagram-icons/upload/${assetId}`),

  // ── Visual-Phase: sticky template/photo defaults per strategy ──────────────
  getVisualDefaults: (strategyId: number) =>
    api.get<{
      template_family: string | null;
      photo_mode: 'off' | 'pool' | 'stock' | 'ai' | null;
      photo_style: string | null;
      effective_template_family?: string | null;
      template_family_source?: 'strategy' | 'niche' | 'niche_keyword' | 'fallback' | null;
    }>(`/strategies/${strategyId}/visual-defaults`),

  updateVisualDefaults: (
    strategyId: number,
    body: {
      template_family?: string | null;
      photo_mode?: 'off' | 'pool' | 'stock' | 'ai' | null;
      photo_style?: string | null;
    },
  ) =>
    api.put<{
      template_family: string | null;
      photo_mode: 'off' | 'pool' | 'stock' | 'ai' | null;
      photo_style: string | null;
    }>(`/strategies/${strategyId}/visual-defaults`, body),

  // Per-generation visual override (does NOT mutate strategy default)
  getGenerationVisuals: (strategyId: number, genId: number) =>
    api.get<{
      effective: {
        template_family: string | null;
        photo_mode: 'off' | 'pool' | 'stock' | 'ai' | null;
        photo_style: string | null;
      };
      override: {
        template_family: string | null;
        photo_mode: string | null;
        photo_style: string | null;
      } | null;
      defaults: {
        template_family: string | null;
        photo_mode: string | null;
        photo_style: string | null;
      };
    }>(`/content-studio/${strategyId}/generations/${genId}/visuals`),

  updateGenerationVisuals: (
    strategyId: number,
    genId: number,
    body: {
      template_family?: string | null;
      photo_mode?: string | null;
      photo_style?: string | null;
    },
  ) =>
    api.put<unknown>(`/content-studio/${strategyId}/generations/${genId}/visuals`, body),

  getGenerationPhotoStatus: (strategyId: number, genId: number) =>
    api.get<{
      slides: Array<{
        slide_number: number;
        photo_status: 'not_requested' | 'pending' | 'assigned' | 'failed';
        photo_id: number | null;
        photo_source: string | null;
        photo_error: string | null;
        photo_assigned_at: string | null;
      }>;
      summary: { pending: number; assigned: number; failed: number; not_requested: number };
    }>(`/content-studio/${strategyId}/generations/${genId}/photo-status`),

  importRemotePhoto: (strategyId: number, genId: number, url: string, source?: string) =>
    api.post<{ ok: boolean; photo: { id: number; disk_path: string; width: number; height: number } }>(
      `/content-studio/${strategyId}/generations/${genId}/import-photo`,
      { url, source: source ?? 'manual' },
    ),
};
